/**
 * The structural editor: days and items, as plain forms over typed ops.
 *
 * Nothing here mutates store.def. Every save is a POST of DefOps under
 * baseRev; the server validates, smoke-tests the engine, remaps state keys in
 * the same transaction, and broadcasts resync — our own socket gets it too,
 * which is what re-renders the page. One write path however the edit is made.
 *
 * Hidden unless the role can edit AND the socket is up: structural edits
 * offline would just queue up 409s.
 */
import type { DefOp } from "@trip/shared/defops";
import { esc } from "@trip/shared/engine/time";
import { isFixed, type DayDef, type DayItem, type SlotItem } from "@trip/shared/types";
import { store } from "../store.js";
import { banner } from "./banner.js";

let editing = false;
export const isEditing = () => editing;

export function toggleEditing(): boolean {
  editing = !editing;
  document.body.classList.toggle("editing", editing);
  return editing;
}

const auth = (extra: Record<string, string> = {}) => ({
  ...extra,
  ...(store.key ? { "x-trip-key": store.key } : {}),
});

/** One door for every editor save; stale rev = someone else moved first. */
export async function postEditorOps(ops: DefOp[]): Promise<boolean> {
  try {
    const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/def`, {
      method: "POST",
      headers: auth({ "content-type": "application/json" }),
      body: JSON.stringify({ baseRev: store.rev, ops }),
    });
    const data = (await res.json()) as { error?: string; code?: string };
    if (!res.ok) {
      banner(data.code === "stale" ? "The trip changed under you — reloading." : (data.error ?? "That edit didn't save."), "warn");
      if (data.code === "stale") setTimeout(() => location.reload(), 1200);
      return false;
    }
    document.getElementById("modalbg")!.classList.remove("open");
    return true;
  } catch {
    banner("Couldn't reach the server.", "warn");
    return false;
  }
}

/* ---------------- edit-mode chrome injected by render.ts ---------------- */

export function dayEditControlsHTML(d: DayDef, idx: number, count: number): string {
  if (!editing) return "";
  return `<span class="ed-ctrl">
    <button class="btn-det btn-sm" data-action="ed-day-edit" data-day="${d.id}" title="Edit this day">✎</button>
    <button class="btn-det btn-sm" data-action="ed-item-add" data-day="${d.id}" title="Add a stop or slot">＋</button>
    ${idx > 0 ? `<button class="btn-det btn-sm" data-action="ed-day-move" data-day="${d.id}" data-dir="-1" title="Move day earlier">↑</button>` : ""}
    ${idx < count - 1 ? `<button class="btn-det btn-sm" data-action="ed-day-move" data-day="${d.id}" data-dir="1" title="Move day later">↓</button>` : ""}
  </span>`;
}

export function itemEditControlsHTML(dayId: string, i: number, count: number): string {
  if (!editing) return "";
  return `<span class="ed-ctrl ed-ctrl-row">
    <button class="btn-det btn-ico" data-action="ed-item-edit" data-day="${dayId}" data-i="${i}" title="Edit">✎</button>
    ${i > 0 ? `<button class="btn-det btn-ico" data-action="ed-item-move" data-day="${dayId}" data-i="${i}" data-dir="-1" title="Move up">↑</button>` : ""}
    ${i < count - 1 ? `<button class="btn-det btn-ico" data-action="ed-item-move" data-day="${dayId}" data-i="${i}" data-dir="1" title="Move down">↓</button>` : ""}
    <button class="btn-det btn-ico" data-action="ed-item-remove" data-day="${dayId}" data-i="${i}" title="Remove">✕</button>
  </span>`;
}

export function addDayButtonHTML(): string {
  return editing ? `<div class="ed-addday"><button class="btn-cta" data-action="ed-day-add">＋ Add day</button></div>` : "";
}

/* ---------------- forms ---------------- */

const modal = () => document.getElementById("modal")!;
const openModalBg = () => document.getElementById("modalbg")!.classList.add("open");

const fld = (id: string, label: string, value: string, extra = "") => `
  <label class="ed-fld"><span>${label}</span>
    <input id="${id}" value="${esc(value)}" ${extra}></label>`;

export function openDayForm(dayId: string | null): void {
  const day = dayId ? store.def.days.find((d) => d.id === dayId) : null;
  const nextIso = (() => {
    const last = store.def.days[store.def.days.length - 1];
    if (!last?.iso) return "";
    const t = new Date(`${last.iso}T12:00:00Z`);
    return new Date(t.getTime() + 86_400_000).toISOString().slice(0, 10);
  })();

  modal().style.maxWidth = "480px";
  modal().innerHTML = `
    <div class="m-head"><div class="m-cat">Edit trip</div><h3>${day ? `Edit ${esc(day.dow)} ${esc(day.date)}` : "Add a day"}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-body"><form id="ed-day-form" novalidate>
      ${fld("edd-title", "Title", day?.title ?? "", 'maxlength="120"')}
      ${fld("edd-loc", "Location", day?.loc ?? "", 'maxlength="80" placeholder="e.g. Dubrovnik"')}
      ${fld("edd-iso", "Date", day?.iso ?? nextIso, 'type="date"')}
      ${fld("edd-start", "Day starts", day?.start ?? "09:00", 'type="time"')}
      <label class="ed-fld"><span>Blurb</span><textarea id="edd-blurb" maxlength="400" rows="2">${esc(day?.blurb ?? "")}</textarea></label>
      <div class="share-copyrow">
        <button class="btn-cta" type="submit">${day ? "Save day" : "Add day"}</button>
        ${day && store.def.days.length > 1 ? `<button class="btn-det" type="button" id="edd-remove">✕ Remove this day</button>` : ""}
      </div>
      <div class="dir-note">${day ? "Removing a day also clears its saved picks — the schedule reflows for everyone." : "New days start with one open “Plans” slot; add stops from there."}</div>
    </form></div>`;
  openModalBg();

  document.getElementById("ed-day-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement).value.trim();
    const iso = v("edd-iso");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return banner("The day needs a date.", "warn");
    if (day) {
      const patch: Record<string, string> = {};
      if (v("edd-title") !== day.title) patch.title = v("edd-title");
      if (v("edd-loc") !== day.loc) patch.loc = v("edd-loc");
      if (iso !== day.iso) patch.iso = iso;
      if (v("edd-start") !== day.start) patch.start = v("edd-start");
      const blurb = (document.getElementById("edd-blurb") as HTMLTextAreaElement).value.trim();
      if (blurb !== day.blurb) patch.blurb = blurb;
      if (!Object.keys(patch).length) return banner("Nothing changed.", "warn");
      if (await postEditorOps([{ op: "day.set", dayId: day.id, patch }])) banner("Day saved.", "ok");
    } else {
      const ok = await postEditorOps([
        { op: "day.add", day: { iso, title: v("edd-title") || undefined, loc: v("edd-loc"), start: v("edd-start"), blurb: (document.getElementById("edd-blurb") as HTMLTextAreaElement).value.trim() } },
      ]);
      if (ok) banner("Day added.", "ok");
    }
  });

  document.getElementById("edd-remove")?.addEventListener("click", async () => {
    if (!day) return;
    if (!confirm(`Remove ${day.dow} ${day.date} and its picks? The schedule reflows for everyone.`)) return;
    if (await postEditorOps([{ op: "day.remove", dayId: day.id }])) banner("Day removed.", "ok");
  });
}

export function openItemForm(dayId: string, index: number | null): void {
  const day = store.def.days.find((d) => d.id === dayId);
  if (!day) return;
  const item = index !== null ? day.items[index] : null;
  const kindFixed = item ? isFixed(item) : false;

  const actRows = Object.entries(store.def.act)
    .filter(([, a]) => !a.nomodal)
    .sort((a, b) => a[1].name.localeCompare(b[1].name))
    .map(
      ([id, a]) => `
      <label class="ed-opt"><input type="checkbox" value="${esc(id)}" ${!kindFixed && (item as SlotItem | null)?.opts.includes(id) ? "checked" : ""}>
        <span>${esc(a.name)}</span></label>`
    )
    .join("");

  modal().style.maxWidth = "540px";
  modal().innerHTML = `
    <div class="m-head"><div class="m-cat">Edit trip</div><h3>${item ? "Edit" : "Add"} — ${esc(day.dow)} ${esc(day.date)}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div class="m-body"><form id="ed-item-form" novalidate>
      ${
        item
          ? ""
          : `<div class="acct-tabs" id="edi-kind">
              <button type="button" class="acct-tab on" data-kind="slot">Choice slot</button>
              <button type="button" class="acct-tab" data-kind="fixed">Fixed stop</button>
            </div>`
      }
      <div id="edi-slot" style="${item && kindFixed ? "display:none" : ""}">
        ${fld("edi-s", "Slot label", !kindFixed ? ((item as SlotItem | null)?.s ?? "") : "", 'maxlength="80" placeholder="e.g. Dinner"')}
        ${fld("edi-min", "Earliest start (optional)", (!kindFixed && (item as SlotItem | null)?.min) || "", 'type="time"')}
        <div class="ed-fld"><span>Options offered</span><div class="ed-opts">${actRows || '<span style="color:var(--mut);font-size:12.5px">No activities yet — the ✨ Suggest button or 🗂 Activities panel adds them.</span>'}</div></div>
        <div class="dir-note">Free-time style sentinels ride along automatically; the default pick is the first option ticked.</div>
      </div>
      <div id="edi-fixed" style="${item && !kindFixed ? "display:none" : item ? "" : "display:none"}">
        ${fld("edi-t", "Time", kindFixed ? ((item as { t?: string })?.t ?? "09:00") : "09:00", 'type="time"')}
        ${fld("edi-e", "End (optional)", (kindFixed && (item as { e?: string })?.e) || "", 'type="time"')}
        ${fld("edi-ic", "Icon", kindFixed ? ((item as { ic?: string })?.ic ?? "📍") : "📍", 'maxlength="4" style="width:70px"')}
        ${fld("edi-l", "Label", kindFixed ? ((item as { l?: string })?.l ?? "") : "", 'maxlength="160" placeholder="e.g. Ferry to Hvar — Jadrolinija 9603"')}
        ${fld("edi-n", "Note (optional)", (kindFixed && (item as { n?: string })?.n) || "", 'maxlength="200"')}
      </div>
      <div class="share-copyrow"><button class="btn-cta" type="submit">${item ? "Save" : "Add"}</button></div>
    </form></div>`;
  openModalBg();

  let kind: "slot" | "fixed" = item ? (kindFixed ? "fixed" : "slot") : "slot";
  document.querySelectorAll<HTMLElement>("#edi-kind [data-kind]").forEach((b) =>
    b.addEventListener("click", () => {
      kind = b.dataset.kind as "slot" | "fixed";
      document.querySelectorAll("#edi-kind .acct-tab").forEach((t) => t.classList.toggle("on", t === b));
      document.getElementById("edi-slot")!.style.display = kind === "slot" ? "" : "none";
      document.getElementById("edi-fixed")!.style.display = kind === "fixed" ? "" : "none";
    })
  );

  document.getElementById("ed-item-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement).value.trim();
    let built: DayItem;
    if (kind === "slot") {
      const opts = [...document.querySelectorAll<HTMLInputElement>(".ed-opts input:checked")].map((x) => x.value);
      // keep whatever sentinels the slot already carried (they're filtered from the checkbox list)
      const sentinels = !kindFixed && item ? (item as SlotItem).opts.filter((o) => store.def.act[o]?.nomodal) : [];
      const all = [...opts, ...sentinels];
      if (!all.length) {
        // a brand-new slot with nothing ticked still needs one option — use the trip's sentinel
        const s = Object.entries(store.def.act).find(([, a]) => a.nomodal)?.[0];
        if (s) all.push(s);
      }
      if (!all.length) return banner("Tick at least one option for the slot.", "warn");
      if (!v("edi-s")) return banner("The slot needs a label.", "warn");
      built = { s: v("edi-s"), opts: all, def: all[0], ...(v("edi-min") ? { min: v("edi-min") } : {}) };
      if (item && !kindFixed) built = { ...(item as SlotItem), s: built.s, opts: all, def: (item as SlotItem).def && all.includes((item as SlotItem).def!) ? (item as SlotItem).def : all[0], min: v("edi-min") || undefined };
    } else {
      if (!v("edi-l")) return banner("The stop needs a label.", "warn");
      built = { ...(item && kindFixed ? (item as object) : {}), f: 1, t: v("edi-t") || "09:00", ic: v("edi-ic") || "📍", l: v("edi-l"), ...(v("edi-e") ? { e: v("edi-e") } : { e: undefined }), ...(v("edi-n") ? { n: v("edi-n") } : { n: undefined }) } as DayItem;
    }
    const ops: DefOp[] =
      index === null
        ? [{ op: "item.add", dayId, item: built }]
        : [{ op: "item.set", dayId, index, patch: built as Partial<DayItem> }];
    if (await postEditorOps(ops)) banner(index === null ? "Added." : "Saved.", "ok");
  });
}

export async function moveItem(dayId: string, i: number, dir: number): Promise<void> {
  await postEditorOps([{ op: "item.move", dayId, from: i, to: i + dir }]);
}

export async function removeItem(dayId: string, i: number): Promise<void> {
  const day = store.def.days.find((d) => d.id === dayId);
  const it = day?.items[i];
  const label = it ? (isFixed(it) ? it.l : it.s) : "this item";
  if (!confirm(`Remove "${label}"? Its saved pick goes too.`)) return;
  if (await postEditorOps([{ op: "item.remove", dayId, index: i }])) banner("Removed.", "ok");
}

export async function moveDay(dayId: string, dir: number): Promise<void> {
  const at = store.def.days.findIndex((d) => d.id === dayId);
  if (at === -1) return;
  await postEditorOps([{ op: "day.move", dayId, to: at + dir }]);
}
